import React, { useEffect, useMemo, useState } from 'react';

// IPT — SandOp Network Backlog Viewer (Vendor Freight) for OLM1.
// Data: IPT_<site> cache (sop_network_viewer_actuals + _forecasts), rows
// [{date, src:'A'|'F', metric, value}]. Pivots metric x date into the daily
// table from the IPT mockup, grouped into weeks with a date header.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function fmtDay(d) {
  if (!d || d.length < 10) return d || '';
  const m = +d.slice(5, 7), dd = +d.slice(8, 10);
  return `${m}-${dd < 10 ? '0' + dd : dd}`;
}
function wdName(d) { return WD[new Date(d + 'T12:00:00').getDay()]; }
function isoWeek(d) {
  const dt = new Date(d + 'T12:00:00');
  const t = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const ys = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return `Wk ${Math.ceil((((t - ys) / 86400000) + 1) / 7)}`;
}
function ptToday() {
  const p = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`;
}
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const fmtVal = (v, kind) => {
  if (v == null || isNaN(v)) return '';
  if (kind === 'pct') return `${v.toFixed(1)}%`;
  if (kind === 'days') return v.toFixed(2); // Days-of-backlog rows show 2 decimals
  return Math.round(v).toLocaleString();
};

// Row definitions: label, src ('A' actuals / 'F' forecasts), metric_name,
// kind ('num' | 'pct' | 'days'). `neg` rows highlight red when negative.
const ROWS = [
  { label: 'Beg Vendor Freight Backlog', src: 'A', metric: 'Beg Vendor Freight Backlog', kind: 'num', band: 'green' },
  { label: 'Forecasted Beg Vendor Freight Backlog', src: 'F', metric: 'Forecasted Beg Vendor Freight Backlog', kind: 'num', band: 'yellow' },
  { label: 'Forecasted Locked Beg Vendor Freight Backlog', src: 'F', metric: 'Forecasted Locked Beg Vendor Freight Backlog', kind: 'num', band: 'yellow' },
  { label: 'New Vendor Freight', src: 'A', metric: 'New Vendor Freight', kind: 'num', band: 'green' },
  { label: 'Forecasted New Vendor Freight', src: 'F', metric: 'Forecasted New Vendor Freight', kind: 'num', band: 'yellow' },
  { label: 'Delta New Vendor Freight', src: 'A', metric: 'Delta New Vendor Freight', kind: 'num', neg: true },
  { label: 'Forecasted APS New Vendor Freight', src: 'F', metric: 'Forecasted APS New Vendor Freight', kind: 'num', band: 'yellow' },
  { label: 'Vendor Receipts', src: 'A', metric: 'Vendor Receipts', kind: 'num', band: 'green' },
  { label: 'PPR Vendor Receipts', src: 'A', metric: 'PPR Vendor Receipts', kind: 'num', band: 'green' },
  { label: 'Forecasted Vendor Receipts', src: 'F', metric: 'Forecasted Vendor Receipts', kind: 'num', band: 'yellow' },
  { label: 'Forecasted Locked Vendor Receipts', src: 'F', metric: 'Forecasted Locked Vendor Receipts', kind: 'num', band: 'yellow' },
  { label: 'Delta Vendor Receipts', src: 'A', metric: 'Delta Vendor Receipts', kind: 'num', neg: true },
  { label: 'Delta Locked Vendor Receipts', src: 'A', metric: 'Delta Locked Vendor Receipts', kind: 'num', neg: true },
  { label: 'End Vendor Freight Backlog', src: 'A', metric: 'End Vendor Freight Backlog', kind: 'num', band: 'green' },
  { label: 'Forecasted End Vendor Freight Backlog', src: 'F', metric: 'Forecasted End Vendor Freight Backlog', kind: 'num', band: 'yellow' },
  { label: 'Forecasted Locked End Vendor Freight Backlog', src: 'F', metric: 'Forecasted Locked End Vendor Freight Backlog', kind: 'num', band: 'yellow' },
  { label: 'Days of End Vendor Freight Backlog', src: 'A', metric: 'Days of End Vendor Freight Backlog', kind: 'days', band: 'green' },
  { label: 'Days of End Vendor Freight Backlog (METIS)', src: 'A', metric: 'Days of End Vendor Freight Backlog (METIS)', kind: 'days', band: 'green' },
  { label: 'Forecasted Days of End Vendor Freight Backlog', src: 'F', metric: 'Forecasted Days of End Vendor Freight Backlog', kind: 'days', band: 'yellow' },
  { label: 'Forecasted Locked Days of End Vendor Freight Backlog', src: 'F', metric: 'Forecasted Locked Days of End Vendor Freight Backlog', kind: 'days', band: 'yellow' },
];

export default function IPT({ api, site }) {
  const [raw, setRaw] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=IPT_${site}&shift_type=data`)
      .then((r) => r.json())
      .then((d) => setRaw(d && Array.isArray(d.rows) ? d : { rows: [] }))
      .catch(() => setRaw({ rows: [] }));
  }, [api, site]);

  const availDates = useMemo(() => {
    const s = new Set(((raw && raw.rows) || []).map((r) => r.date));
    return [...s].sort();
  }, [raw]);

  // Default range: a week back .. next Sunday-ish (mirrors the mockup's 2-week view).
  useEffect(() => {
    if (!availDates.length) return;
    if (!from) setFrom(addDays(ptToday(), -8));
    if (!to) setTo(addDays(ptToday(), 4));
  }, [availDates]); // eslint-disable-line

  const dates = useMemo(
    () => availDates.filter((d) => (!from || d >= from) && (!to || d <= to)),
    [availDates, from, to]
  );

  // pivot: { 'src|metric': { date: value } }
  const pivot = useMemo(() => {
    const p = {};
    for (const r of (raw && raw.rows) || []) {
      const k = `${r.src}|${r.metric}`;
      (p[k] = p[k] || {})[r.date] = r.value;
    }
    return p;
  }, [raw]);

  // group dates into weeks for the two-tier header
  const weekSpans = useMemo(() => {
    const spans = []; let cur = null;
    for (const d of dates) {
      const w = isoWeek(d);
      if (!cur || cur.week !== w) { cur = { week: w, dates: [] }; spans.push(cur); }
      cur.dates.push(d);
    }
    return spans;
  }, [dates]);

  const cell = { border: '1px solid #ddd', padding: '2px 8px', fontSize: '0.72rem', whiteSpace: 'nowrap' };
  const val = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const bandBg = { green: '#e7f3e8', yellow: '#fbf8e3', none: '#fff' };

  return (
    <div>
      <h2 className="section-title">IPT — SandOp Network Backlog Viewer (Vendor Freight) — {site}</h2>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>From: <input type="date" value={from} min={availDates[0]} max={availDates[availDates.length - 1]} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>To: <input type="date" value={to} min={availDates[0]} max={availDates[availDates.length - 1]} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <span style={{ fontSize: '0.7rem', color: '#555' }}>{dates.length} day(s)</span>
        {raw && raw.generatedAt && <span style={{ fontSize: '0.65rem', color: '#888' }}>synced hourly · last {String(raw.generatedAt).slice(0, 16).replace('T', ' ')}Z</span>}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...cell, background: '#fff', position: 'sticky', left: 0, minWidth: 260 }} />
              {weekSpans.map((w) => (
                <th key={w.week} colSpan={w.dates.length} style={{ ...cell, background: '#fff', textAlign: 'center', fontWeight: 700 }}>{w.week}</th>
              ))}
            </tr>
            <tr>
              <th style={{ ...cell, background: '#fff', textAlign: 'left', position: 'sticky', left: 0, fontWeight: 700 }}>Metric</th>
              {dates.map((d) => (
                <th key={d} style={{ ...cell, background: '#fff', textAlign: 'center', fontWeight: 700 }}>{wdName(d)}<br />{fmtDay(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const series = pivot[`${row.src}|${row.metric}`] || {};
              const bg = bandBg[row.band || 'none'];
              return (
                <tr key={row.label}>
                  <td style={{ ...cell, textAlign: 'left', background: bg, fontWeight: 600, position: 'sticky', left: 0 }}>{row.label}</td>
                  {dates.map((d) => {
                    const v = series[d];
                    const neg = row.neg && v != null && v < 0;
                    return (
                      <td key={d} style={{ ...val, background: neg ? '#fdd9d7' : bg, color: neg ? '#c0392b' : '#000' }}>
                        {fmtVal(v, row.kind)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
